import { UUID } from "@bitory-gmbh/nidavellir";
import { documentDbPut } from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Query,
  useDocumentDBGetAll,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import {
  useLazySamplesDeviceListQuery,
  useSamplesDeviceListQuery,
} from "@/services/backend/samples/api/device-api";
import { Device } from "@/services/backend/samples/types/device";
import { useParams } from "react-router";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

export function useDevicesOffline(workOrderId: UUID) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [load] = useLazySamplesDeviceListQuery();

  const prepMsg = t("Speichere Geräte ...");
  const doneMsg = t("Geräte");

  const prepare = async () => {
    const result = await load({});
    if (result.error) throw result.error;

    addStale("devices");

    setSynced((prev) => [...prev, prepMsg]);

    await Promise.all(
      (result.data!.devices ?? []).map(async (item) => {
        await documentDbPut<Device>(
          GSMDocumentKind.WorkOrder,
          workOrderId,
          "devices",
          item,
        );
      }),
    );

    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  return { prepare };
}

export function useGetDevices(): Query<Device[]> {
  const { offlineState } = useOfflineContext();
  const { workOrderId } = useParams();

  const {
    data: deviceList,
    isLoading: deviceIsLoading,
    error: deviceError,
  } = useSamplesDeviceListQuery({});
  const devices = deviceList?.devices ?? [];

  const {
    data: indexedDBData,
    isLoading: indexedDBIsLoading,
    error: indexedDBError,
  } = useDocumentDBGetAll<Device>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "devices",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: devices,
        isLoading: deviceIsLoading,
        error: deviceError,
      };
    case OfflineState.OFFLINE:
      return {
        data: indexedDBData,
        isLoading: indexedDBIsLoading,
        error: indexedDBError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}
